import { api } from './axios'
import type {
  AbortUploadResponseDto,
  BatchFileUploadRequestDto,
  BatchFileUploadResponseDto,
  BulkMoveFileDto,
  BulkMoveResponseDto,
  BulkOperationDto,
  BulkOperationResponseDto,
  BulkUploadCompleteRequestDto,
  CompleteMultipartUploadDto,
  CompleteMultipartUploadResponseDto,
  CompleteProfilePictureUploadDto,
  CompleteProfilePictureUploadResponseDto,
  CompleteSingleUploadDto,
  CompleteSingleUploadResponseDto,
  CopyFilesDto,
  CreateFolderDto,
  CreateFolderResponseDto,
  DeleteProfilePictureResponseDto,
  DownloadInfoDto,
  DownloadRequestResponseDto,
  FileListingItemDto,
  FilesSearchDto,
  FilesSearchResponse,
  FolderUploadCancelResponseDto,
  FolderUploadCompleteResponseDto,
  FolderUploadInitiateDto,
  FolderUploadInitiateResponseDto,
  GetProfilePictureResponseDto,
  LookupFileResponseDto,
  MediaType,
  MultipartFileSignedUrlsResponseDto,
  ResumeMultipartUploadUrlDto,
  RetryFileEncryptionResponseDto,
  StartMultipartUploadResponseDto,
  StartMultipartUploadUrlDto,
  StartProfilePictureUploadDto,
  StartProfilePictureUploadResponseDto,
  StartSingleUploadResponseDto,
  StartSingleUploadUrlDto,
  UpdateFileDto,
  UpdateFileResponseDto,
} from './types'

const USE_MOCK_DATA = false

// Single File Upload
/**
 * Initiates a single file upload process and returns a pre-signed URL for direct upload to S3
 */
export const startSingleUpload = async (
  data: StartSingleUploadUrlDto,
): Promise<StartSingleUploadResponseDto> => {
  const response = await api.post<StartSingleUploadResponseDto>('/files/upload/single/start', data)

  return response.data
}

/**
 * Complete single file upload
 * Finalizes a single file upload process by updating file metadata with size and ETag
 */
export const completeSingleUpload = async (
  data: CompleteSingleUploadDto,
): Promise<CompleteSingleUploadResponseDto> => {
  const response = await api.patch<CompleteSingleUploadResponseDto>(
    '/files/upload/single/complete',
    data,
  )

  return response.data
}

// Profile Picture Upload
/**
 * Start profile picture upload
 * Initiates a profile picture upload process and returns a pre-signed URL for direct upload to S3
 */
export const startProfilePictureUpload = async (
  data: StartProfilePictureUploadDto,
): Promise<StartProfilePictureUploadResponseDto> => {
  const response = await api.post<StartProfilePictureUploadResponseDto>(
    '/users/me/profile-picture/upload/start',
    data,
  )

  return response.data
}

/**
 * Complete profile picture upload
 * Finalizes a profile picture upload process and triggers thumbnail generation
 */
export const completeProfilePictureUpload = async (
  data: CompleteProfilePictureUploadDto,
): Promise<CompleteProfilePictureUploadResponseDto> => {
  const response = await api.patch<CompleteProfilePictureUploadResponseDto>(
    '/users/me/profile-picture/upload/complete',
    data,
  )

  return response.data
}

/**
 * Get profile picture
 * Retrieves the current profile picture information
 */
export const getProfilePicture = async (): Promise<GetProfilePictureResponseDto> => {
  const response = await api.get<GetProfilePictureResponseDto>('/users/me/profile-picture')

  return response.data
}

/**
 * Delete profile picture
 * Removes the current profile picture and restores the default avatar
 */
export const deleteProfilePicture = async (): Promise<DeleteProfilePictureResponseDto> => {
  const response = await api.delete<DeleteProfilePictureResponseDto>('/users/me/profile-picture')

  return response.data
}

// Multipart File Upload
/**
 * Start multipart file upload
 * Initiates a multipart file upload process and optionally returns pre-signed URLs for each part
 */
export const startMultipartUpload = async (
  data: StartMultipartUploadUrlDto,
): Promise<StartMultipartUploadResponseDto> => {
  const response = await api.post<StartMultipartUploadResponseDto>(
    '/files/upload/multipart/start',
    data,
  )

  return response.data
}

/**
 * Resume multipart upload
 * Generates a pre-signed URL for uploading a specific part of a multipart upload
 */
export const getMultipartFileSignedUrls = async (
  data: ResumeMultipartUploadUrlDto,
): Promise<MultipartFileSignedUrlsResponseDto> => {
  const response = await api.post<MultipartFileSignedUrlsResponseDto>(
    '/files/upload/multipart/get-urls',
    data,
  )

  return response.data
}

/**
 * Complete multipart upload
 * Finalizes a multipart upload process by combining all uploaded parts
 */
export const completeMultipartUpload = async (
  data: CompleteMultipartUploadDto,
): Promise<CompleteMultipartUploadResponseDto> => {
  const response = await api.patch<CompleteMultipartUploadResponseDto>(
    '/files/upload/multipart/complete',
    data,
  )

  return response.data
}

// Folder Operations
/**
 * Create new folder
 * Creates a new folder in the specified location
 */
export const createFolder = async (data: CreateFolderDto): Promise<CreateFolderResponseDto> => {
  const response = await api.post<CreateFolderResponseDto>('/files/folder', data)

  return response.data
}

/**
 * Initiate folder upload
 * Initiates a folder upload process by creating the complete folder hierarchy instantly
 */
export const initiateFolderUpload = async (
  data: FolderUploadInitiateDto,
): Promise<FolderUploadInitiateResponseDto> => {
  // Make the API call
  const response = await api.post<FolderUploadInitiateResponseDto>(
    '/files/folder/upload/initiate',
    data,
  )

  return response.data
}

/**
 * Start batch file upload
 * Creates file records for a batch of files and returns presigned URLs for upload
 */
export const startBatchFileUpload = async (
  folderId: string,
  data: BatchFileUploadRequestDto,
): Promise<BatchFileUploadResponseDto> => {
  // Make the API call
  const response = await api.post<BatchFileUploadResponseDto>(
    `/files/folder/${folderId}/upload/start-batch`,
    data,
  )

  return response.data
}

/**
 * Cancel folder upload
 * Cancels an ongoing folder upload session and cleans up resources
 */
export const cancelFolderUpload = async (
  folderId: string,
): Promise<FolderUploadCancelResponseDto> => {
  // Make the API call
  const response = await api.post<FolderUploadCancelResponseDto>(
    `/files/folder/${folderId}/upload/cancel`,
  )

  return response.data
}

/**
 * Complete folder upload
 * Completes an ongoing folder upload session
 */
export const completeFolderUpload = async (
  folderId: string,
): Promise<FolderUploadCompleteResponseDto> => {
  // Make the API call
  const response = await api.post<FolderUploadCompleteResponseDto>(
    `/files/folder/${folderId}/upload/complete`,
  )

  return response.data
}

/**
 * Get folder details with breadcrumb and children
 * Retrieve detailed information about a folder including its breadcrumb navigation and child files/folders
 */
export const getFolderDetails = async (params: {
  id?: string
  path?: string
  limit?: number
  offset?: number
  search?: string
  mediaType?: MediaType
  sortBy?: 'name' | 'createdAt' | 'updatedAt' | 'sizeBytes'
  sortOrder?: 'ASC' | 'DESC'
  createdFrom?: string
  createdTo?: string
  location?: string
}): Promise<FilesSearchResponse> => {
  const response = await api.get<FilesSearchResponse>('/files/folder', {
    params,
  })

  return response.data
}

// File Operations
/**
 * Get file or folder details
 * Retrieve details of a specific file or folder by ID or path
 */
export const getFile = async (params: {
  id?: string
  path?: string
}): Promise<FileListingItemDto> => {
  const response = await api.get<FileListingItemDto>('/files/file', {
    params,
  })

  return response.data
}

/**
 * Lookup file by name in parent directory
 * Check if a file with the specified name exists in the given parent directory
 */
export const lookupFile = async (params: {
  name: string
  parentId?: string
  parentPath?: string
}): Promise<LookupFileResponseDto> => {
  const response = await api.get<LookupFileResponseDto>('/files/file/exists', {
    params,
  })

  return response.data
}

/**
 * List files and folders
 * List files and folders with support for filtering, searching, sorting, and pagination
 */
export const searchFiles = async (data: FilesSearchDto): Promise<FilesSearchResponse> => {
  if (USE_MOCK_DATA) {
    return getMockFilesSearch(data)
  }
  const response = await api.post<FilesSearchResponse>('/files/search', data)

  return response.data
}

/**
 * Update file or folder
 * Update file or folder details such as name or favorite status
 */
// Track ongoing requests to prevent duplicates
const ongoingRequests = new Map<string, Promise<UpdateFileResponseDto>>()

export const updateFile = async (
  data: UpdateFileDto,
  params: { id?: string; path?: string },
): Promise<UpdateFileResponseDto> => {
  // Validate that at least one parameter is provided
  if (!params.id && !params.path) {
    throw new Error('Either id or path parameter is required')
  }

  // Validate that at least one field is provided for update
  if (!data.name) {
    throw new Error('At least one field (name) must be provided for update')
  }

  // Validate file ID format if provided
  if (
    params.id &&
    !/^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(params.id)
  ) {
    console.warn('File ID format may be invalid:', params.id)
  }

  // Create a unique key for this request
  const requestKey = `${params.id || params.path}-${JSON.stringify(data)}`

  // Check if there's already an ongoing request for this file with the same data
  if (ongoingRequests.has(requestKey)) {
    console.log('🔄 Returning existing request for:', requestKey)

    return ongoingRequests.get(requestKey)!
  }

  console.log('🆕 Creating new request for:', { data, params })

  // Create the request promise
  const requestPromise = (async () => {
    try {
      // Ensure the request body is properly formatted
      const requestBody = {
        ...data,
      }

      const response = await api.patch<UpdateFileResponseDto>('/files/file', requestBody, {
        params,
      })

      return response.data
    } finally {
      // Clean up the ongoing request
      ongoingRequests.delete(requestKey)
    }
  })()

  // Store the request promise
  ongoingRequests.set(requestKey, requestPromise)

  return requestPromise
}

/**
 * Retry file encryption
 * Retries encryption for a file that failed to encrypt
 */
export const retryFileEncryption = async (
  fileId: string,
): Promise<RetryFileEncryptionResponseDto> => {
  const response = await api.post<RetryFileEncryptionResponseDto>(`/files/${fileId}/retry`)

  return response.data
}

// Download Operations
/**
 * Request file download
 * Initiates a download request for a file
 */
export const requestDownload = async (params: {
  id?: string
  path?: string
}): Promise<DownloadRequestResponseDto> => {
  const response = await api.post<DownloadRequestResponseDto>('/files/download', {}, { params })

  return response.data
}

/**
 * Get file download status and details
 * Retrieves the current status and details of a download request for a specific file
 */
export const findDownload = async (params: {
  id?: string
  path?: string
}): Promise<DownloadInfoDto> => {
  const response = await api.get<DownloadInfoDto>('/files/download', {
    params,
  })

  return response.data
}

// Bulk Operations
/**
 * Bulk move files
 * Move multiple files to a different location
 */
export const bulkMoveFiles = async (data: BulkMoveFileDto): Promise<BulkMoveResponseDto> => {
  if (USE_MOCK_DATA) {
    await new Promise(resolve => setTimeout(resolve, 1000))

    return {
      successCount: data.fileIds.length,
      failureCount: 0,
      successfulIds: data.fileIds,
      failures: [],
      conflictCount: 0,
      conflictedFiles: [],
    }
  }

  const response = await api.post<BulkMoveResponseDto>('/files/bulk/move', data)

  return response.data
}

/**
 * Bulk copy files
 * Copy multiple files to the same folder location
 */
export const bulkCopyFiles = async (data: CopyFilesDto): Promise<BulkOperationResponseDto> => {
  const response = await api.post<BulkOperationResponseDto>('/files/bulk/copy', data)

  return response.data
}

/**
 * Bulk move files to trash
 * Moves multiple files and folders to trash (soft delete) in a single operation
 */
export const bulkMoveToTrash = async (
  data: BulkOperationDto,
): Promise<BulkOperationResponseDto> => {
  const response = await api.post<BulkOperationResponseDto>('/files/bulk/move-to-trash', data)

  return response.data
}

/**
 * Bulk restore files from trash
 * Restores multiple files and folders from trash to their original locations
 */
export const bulkRestore = async (data: BulkOperationDto): Promise<BulkOperationResponseDto> => {
  const response = await api.post<BulkOperationResponseDto>('/files/bulk/restore', data)

  return response.data
}

/**
 * Bulk permanent delete
 * Permanently delete multiple files from trash
 */
export const bulkPermanentDelete = async (
  data: BulkOperationDto,
): Promise<BulkOperationResponseDto> => {
  const response = await api.post<BulkOperationResponseDto>('/files/bulk/permanent-delete', data)

  return response.data
}

/**
 * Bulk toggle favorites
 * Toggle favorite status for multiple files
 */
export const bulkToggleFavorite = async (
  data: BulkOperationDto,
): Promise<BulkOperationResponseDto> => {
  const response = await api.post<BulkOperationResponseDto>('/files/bulk/favorite/toggle', data)

  return response.data
}

/**
 * Abort file upload
 * Aborts a file upload and cleans up associated resources. File must be in UPLOADING status.
 */
export const abortFileUpload = async (fileId: string): Promise<AbortUploadResponseDto> => {
  const response = await api.post<AbortUploadResponseDto>(`/files/${fileId}/abort`)

  return response.data
}

/**
 * Bulk complete file uploads
 * Complete multiple file uploads in a single request for improved performance
 */
export const bulkCompleteUploads = async (
  folderId: string,
  data: BulkUploadCompleteRequestDto,
): Promise<BulkOperationResponseDto> => {
  // Deduplicate completions by ID to prevent duplicate IDs from being sent
  const seenIds = new Set<string>()
  const deduplicatedCompletions = data.completions.filter(completion => {
    if (seenIds.has(completion.id)) {
      return false
    }

    seenIds.add(completion.id)

    return true
  })

  // Make the API call with deduplicated data
  const response = await api.post<BulkOperationResponseDto>(
    `/files/folder/${folderId}/upload/complete-batch`,
    {
      ...data,
      completions: deduplicatedCompletions,
    },
  )

  return response.data
}

/**
 * Empty trash
 * Permanently deletes all files and folders currently in the trash
 */
export const emptyTrash = async (): Promise<BulkOperationResponseDto> => {
  const response = await api.post<BulkOperationResponseDto>('/files/trash/empty')

  return response.data
}

/**
 * Bulk abort uploads
 * Aborts multiple uploads in a single operation. The operation returns detailed success/failure information for each upload. All files must be in UPLOADING or ENCRYPTING status.
 */
export const bulkAbortUploads = async (
  data: BulkOperationDto,
): Promise<BulkOperationResponseDto> => {
  const response = await api.post<BulkOperationResponseDto>('/files/bulk/abort', data)

  return response.data
}

/**
 * Generic API call utility for file management endpoints
 * Usage: callFileManagementEndpoint('POST', '/files/search', { ...data })
 */
export const callFileManagementEndpoint = async <T = any>(
  method: 'GET' | 'POST' | 'PATCH' | 'DELETE',
  path: string,
  data?: any,
  params?: any,
): Promise<T> => {
  switch (method) {
    case 'GET':
      return (await api.get<T>(path, { params })).data
    case 'POST':
      return (await api.post<T>(path, data, { params })).data
    case 'PATCH':
      return (await api.patch<T>(path, data, { params })).data
    case 'DELETE':
      return (await api.delete<T>(path, { params })).data
    default:
      throw new Error(`Unsupported method: ${method}`)
  }
}

// Separate mock functions
const getMockFilesSearch = async (_data: any): Promise<FilesSearchResponse> => {
  await new Promise(resolve => setTimeout(resolve, 300))

  // ... mock implementation
  return {
    pageInfo: {
      hasPreviousPage: false,
      hasNextPage: false,
      startOffset: 0,
      endOffset: 0,
      totalPages: 0,
      count: 0,
    },
    totalCount: 0,
    data: [],
  }
}
